'use client';

import { useEffect, useState, useRef } from 'react';
import Link from 'next/link';

interface TrendingMessage {
  id: string;
  content: string;
  send_count: number;
  created_at: string;
  slug?: string | null;
}

interface VsPair {
  slug_a: string;
  slug_b: string;
  send_count_a: number;
  send_count_b: number;
  total_sends: number;
  content_a: string;
  content_b: string;
}

export default function TrendingPage() {
  const [messages, setMessages] = useState<TrendingMessage[]>([]);
  const [vsPairs, setVsPairs] = useState<VsPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'campaigns' | 'vs'>('campaigns');

  // VS search state
  const [vsQuery, setVsQuery] = useState('');
  const [vsResults, setVsResults] = useState<VsPair[]>([]);
  const [vsSearching, setVsSearching] = useState(false);
  const vsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Read tab from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') === 'vs') setTab('vs');
  }, []);

  // Initial data fetch
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [msgRes, vsRes] = await Promise.all([
          fetch('/api/messages/trending'),
          fetch('/api/vs-trending?limit=20'),
        ]);
        if (msgRes.ok) setMessages(await msgRes.json());
        if (vsRes.ok) {
          const pairs = await vsRes.json();
          setVsPairs(pairs);
          setVsResults(pairs);
        }
      } catch (error) {
        console.error("Failed to load trending", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Debounced VS search
  useEffect(() => {
    if (vsDebounceRef.current) clearTimeout(vsDebounceRef.current);
    vsDebounceRef.current = setTimeout(async () => {
      setVsSearching(true);
      try {
        const url = vsQuery.trim()
          ? `/api/vs-trending?limit=20&q=${encodeURIComponent(vsQuery)}`
          : '/api/vs-trending?limit=20';
        const data = await fetch(url).then(r => r.json());
        setVsResults(Array.isArray(data) ? data : []);
      } catch {
        // keep existing results
      } finally {
        setVsSearching(false);
      }
    }, 300);
  }, [vsQuery]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center text-white">
        <div className="flex flex-col items-center animate-pulse">
          <div className="w-16 h-16 border-4 border-[#1e3050] border-t-amber-500 rounded-full animate-spin mb-6"></div>
          <p className="text-xl font-bold">Loading Trending Campaigns...</p>
        </div>
      </main>
    );
  }

  const topThree = messages.slice(0, 3);
  const theRest = messages.slice(3, 20);

  return (
    <main className="min-h-screen flex flex-col items-center p-4 sm:p-6 font-sans pb-20">

      {/* HEADER */}
      <div className="max-w-5xl w-full mt-12 sm:mt-20 mb-8 text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
          Trending <span className="bg-gradient-to-r from-red-500 to-amber-400 bg-clip-text text-transparent">Campaigns</span>
        </h1>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
          {tab === 'campaigns'
            ? 'The most impactful constituent messages from the last 7 days. Select a campaign to add your name and mail it directly to your representatives.'
            : 'The most contested issues on PostcardsToCongress. See which side is winning.'}
        </p>
      </div>

      {/* TAB SWITCHER */}
      <div className="flex gap-2 justify-center mb-10">
        <button
          onClick={() => { setTab('campaigns'); window.history.replaceState(null, '', '?tab=campaigns'); if (typeof window.umami !== 'undefined') window.umami.track('trending_tab_switched', { tab: 'campaigns' }); }}
          className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all ${
            tab === 'campaigns'
              ? 'bg-amber-500 text-[#07111e]'
              : 'bg-[#111f35] text-slate-400 hover:text-white border border-amber-500/20'
          }`}
        >
          🔥 Campaigns
        </button>
        <button
          onClick={() => { setTab('vs'); window.history.replaceState(null, '', '?tab=vs'); if (typeof window.umami !== 'undefined') window.umami.track('trending_tab_switched', { tab: 'vs' }); }}
          className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all ${
            tab === 'vs'
              ? 'bg-amber-500 text-[#07111e]'
              : 'bg-[#111f35] text-slate-400 hover:text-white border border-amber-500/20'
          }`}
        >
          ⚔️ VS Battles
        </button>
      </div>

      <div className="max-w-6xl w-full space-y-16">

        {tab === 'campaigns' && (
          <>
            {/* THE PODIUM: TOP 3 */}
            {topThree.length > 0 && (
              <section className="animate-in fade-in zoom-in-95 duration-700 delay-100">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2 border-b border-amber-500/15 pb-4">
                  🏆 Top Campaigns This Week
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                  {topThree.map((msg, index) => (
                    <div
                      key={msg.id}
                      className="group relative bg-white rounded-3xl p-6 sm:p-8 shadow-xl hover:shadow-2xl hover:shadow-amber-900/20 transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-amber-200 flex flex-col h-full"
                    >
                      <div className="absolute -top-5 -left-5 w-12 h-12 bg-[#07111e] text-white rounded-full flex items-center justify-center font-bold text-xl border-4 border-slate-100 shadow-md group-hover:bg-amber-500 transition-colors">
                        #{index + 1}
                      </div>
                      <div className="flex justify-between items-start mb-4 mt-2">
                        <span className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                          🔥 {msg.send_count} Sent
                        </span>
                      </div>
                      <Link href={`/m/${msg.slug || msg.id}`} className="flex-grow">
                        <p className="text-slate-800 font-serif text-lg leading-relaxed line-clamp-6">
                          &ldquo;{msg.content}&rdquo;
                        </p>
                      </Link>
                      <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
                        <Link href={`/m/${msg.slug || msg.id}`} className="text-amber-600 font-bold text-sm group-hover:text-amber-700 flex items-center gap-1">
                          Join Campaign <span>→</span>
                        </Link>
                        <Link href={`/vs?a=${msg.slug || msg.id}`} className="text-xs text-slate-400 hover:text-amber-500 transition-colors">
                          ⚔️ Compare
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* THE REST: 4 THROUGH 20 */}
            {theRest.length > 0 && (
              <section className="animate-in fade-in slide-in-from-bottom-8 duration-700 delay-300">
                <h2 className="text-2xl font-bold text-white mb-6 border-b border-amber-500/15 pb-4">
                  More Active Campaigns
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {theRest.map((msg, index) => (
                    <div
                      key={msg.id}
                      className="group bg-[#111f35]/50 hover:bg-[#111f35] rounded-2xl p-5 border border-amber-900/25 hover:border-amber-700/30 transition-all flex items-start gap-4"
                    >
                      <div className="text-amber-700/60 font-bold text-lg min-w-[2rem] pt-1 group-hover:text-amber-500/80 transition-colors">
                        #{index + 4}
                      </div>
                      <div className="flex-grow">
                        <Link href={`/m/${msg.slug || msg.id}`}>
                          <p className="text-slate-200 font-serif text-base line-clamp-3 mb-3">
                            &ldquo;{msg.content}&rdquo;
                          </p>
                        </Link>
                        <div className="flex items-center gap-3 text-xs font-medium">
                          <span className="text-slate-400 bg-[#07111e] px-2 py-1 rounded-md">
                            {msg.send_count} Sent
                          </span>
                          <Link href={`/m/${msg.slug || msg.id}`} className="text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity">
                            Send this →
                          </Link>
                          <Link href={`/vs?a=${msg.slug || msg.id}`} className="text-slate-500 hover:text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity">
                            ⚔️ Compare
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* EMPTY STATE */}
            {messages.length === 0 && (
              <div className="text-center py-20 bg-[#111f35]/50 rounded-3xl border border-amber-900/25">
                <h3 className="text-2xl font-bold text-white mb-2">No campaigns yet this week.</h3>
                <p className="text-slate-400 mb-6">Be the first to start a movement.</p>
                <Link href="/" className="px-8 py-4 bg-red-700 hover:bg-red-800 text-white font-bold rounded-xl transition-colors">
                  Write a Postcard
                </Link>
              </div>
            )}
          </>
        )}

        {tab === 'vs' && (
          <section className="animate-in fade-in slide-in-from-bottom-8 duration-500">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2 border-b border-amber-500/15 pb-4">
              ⚔️ Top VS Battles
            </h2>

            {/* SEARCH */}
            <div className="relative mb-6">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg">🔍</span>
              <input
                type="text"
                value={vsQuery}
                onChange={e => setVsQuery(e.target.value)}
                placeholder="Search VS battles by topic or message content..."
                className="w-full pl-11 pr-10 py-3.5 rounded-xl border border-amber-500/20 bg-[#0a1628] text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400/40 outline-none text-sm transition-all"
              />
              {vsQuery && (
                <button
                  onClick={() => setVsQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors text-lg leading-none"
                  aria-label="Clear search"
                >✕</button>
              )}
            </div>

            {/* RESULTS */}
            {vsSearching ? (
              <div className="flex justify-center py-10">
                <div className="w-8 h-8 border-2 border-[#1e3050] border-t-amber-500 rounded-full animate-spin" />
              </div>
            ) : vsResults.length > 0 ? (
              <div className="flex flex-col gap-3">
                {vsResults.map((pair, index) => {
                  const excerptA = pair.content_a.length > 80 ? pair.content_a.slice(0, 79) + '…' : pair.content_a;
                  const excerptB = pair.content_b.length > 80 ? pair.content_b.slice(0, 79) + '…' : pair.content_b;
                  return (
                    <Link
                      key={`${pair.slug_a}-${pair.slug_b}`}
                      href={`/vs/${pair.slug_a}/${pair.slug_b}`}
                      className="group bg-[#111f35]/50 hover:bg-[#111f35] rounded-2xl p-4 border border-amber-900/25 hover:border-amber-700/30 transition-all flex items-center gap-3"
                    >
                      <div className="text-amber-700/60 font-bold text-lg min-w-[2rem] group-hover:text-amber-500/80 transition-colors shrink-0">
                        #{index + 1}
                      </div>
                      <div className="flex-1 flex items-center gap-2 min-w-0">
                        <p className="text-blue-300 text-sm flex-1 truncate">{excerptA}</p>
                        <span className="text-amber-500 font-extrabold text-xs shrink-0 px-2">VS</span>
                        <p className="text-red-300 text-sm flex-1 truncate">{excerptB}</p>
                      </div>
                      <div className="shrink-0 flex items-center gap-2 text-xs">
                        <span className="text-slate-400 bg-[#07111e] px-2 py-1 rounded-md whitespace-nowrap">
                          {pair.total_sends} sent
                        </span>
                        <span className="text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20 bg-[#111f35]/50 rounded-3xl border border-amber-900/25">
                {vsQuery.trim() ? (
                  <>
                    <h3 className="text-2xl font-bold text-white mb-2">No VS battles found.</h3>
                    <p className="text-slate-400">Try different keywords.</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-white mb-2">No VS battles yet.</h3>
                    <p className="text-slate-400 mb-6">Be the first to pit two campaigns against each other.</p>
                    <Link href="/vs" className="px-8 py-4 bg-gradient-to-r from-blue-700 to-red-700 hover:from-blue-600 hover:to-red-600 text-white font-bold rounded-xl transition-all">
                      Start a Battle →
                    </Link>
                  </>
                )}
              </div>
            )}
          </section>
        )}

      </div>
    </main>
  );
}
